import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { useCanShowDeliveredBySellarOptionV2 } from "./useCanShowDeliveredBySellarOptionV2";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { useShallow } from "zustand/shallow";
import { useSellarCurrentBusiness } from "~/hooks/useSellarCurrentBusiness.shared";
import { unAuthorisedUserPostcodeInLocalStorageKey } from "../DiscoveryV2Home";
import { useGetUpcomingDeliveryGroups } from "./useGetUpcomingDeliveryGroups";
import dayjs from "dayjs";
import {
  Card,
  DataButton,
  H5,
  H6,
  Icon,
  ImageWithFallback,
  InputContainer,
  InputOption,
  InputSelect,
  ProfilePicture,
  SingleFieldDialog,
  SingleFieldDialogImage,
} from "@sellar-io/components";
import { useState } from "react";
import { useSupplierAddToList } from "~/hooks/useSupplierAddToList";
import { useSupplier } from "~/hooks/api/v2/supplier/useSupplier";
import { useSupplierAddToFavourites } from "~/hooks/useSupplierAddToFavourites";
import { useSellarDistributionAvailabilityUpcomingDeliveryList } from "~/hooks/api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityUpcomingDeliveryList";
import {
  ProfilePictureCollection,
  ProfilePictureCollectionImage,
} from "~/components/profilePictureCollection/profilePictureCollection";
import { twMerge } from "tailwind-merge";
import isoWeek from "dayjs/plugin/isoWeek";
import calendar from "dayjs/plugin/calendar";
import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { OrderBySelector } from "./OrderBySelector";

dayjs.extend(isoWeek);
dayjs.extend(calendar);

const now = new Date();
const fridayOfCurrentWeek = dayjs(now).isoWeekday(5).endOf("day").toDate();

export const DeliveredBySellarMarketHeader = () => {
  const canShowDeliveredBySellarOptionV2 =
    useCanShowDeliveredBySellarOptionV2();
  const isMobile = useMediaQuery("(max-width: 1024px)");

  const { selectedSuppliers, isDeliveredBySellar } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      selectedSuppliers: state.selectedSuppliers,
      isDeliveredBySellar: state.isDeliveredBySellar,
    })),
  );

  if (!canShowDeliveredBySellarOptionV2) return null;

  const selectedSupplierId = selectedSuppliers[0]?.id;

  const canShowSingleSupplierHeader =
    selectedSuppliers.length === 1 && isDeliveredBySellar;

  return (
    <div className="mx-auto mt-4 mb-2 flex w-full max-w-300 flex-col gap-2 px-4">
      {!canShowSingleSupplierHeader && !isMobile && isDeliveredBySellar && (
        <>
          <DeliveredBySellarSearchHeader />
          <DeliveredBySellarHeaderWithCutOffToggle isMobile={isMobile} />
        </>
      )}
      {canShowSingleSupplierHeader && selectedSupplierId && (
        <DeliveredBySellarSingleSupplierHeader
          supplierId={selectedSupplierId}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};

const DeliveredBySellarSearchHeader = () => {
  return (
    <div className="flex rounded-lg bg-white p-4">
      <div className="flex flex-1 flex-col">
        <div className="text-xl font-semibold">
          Delivered by <span className="text-[#8C23C7]">Sellar</span> search
        </div>
        <p className="text-zinc-600">
          Check what's available by order cut-off time and use the filters
          provided to narrow down your results
        </p>
      </div>
      <OrderBySelector />
    </div>
  );
};

export const DeliveredBySellarHeaderWithCutOffToggle = ({
  isMobile,
  inputContainerClassName,
}: {
  isMobile: boolean;
  inputContainerClassName?: string;
}) => {
  const { business } = useSellarCurrentBusiness();

  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    unAuthorisedUserPostcodeInLocalStorageKey,
    undefined,
  );
  const buyerPostcode =
    business?.DeliveryAddress.postalCode || unAuthorisedUserPostcode || "";

  const { orderCutOffAt, deliveredBySellarOption, setOrderCutOffAt } =
    useDiscoveryV2SearchStore(
      useShallow((state) => ({
        orderCutOffAt: state.orderCutOffAt,
        deliveredBySellarOption: state.deliveredBySellarOption,
        setOrderCutOffAt: state.setOrderCutOffAt,
      })),
    );

  const { upcomingDeliveryGroups } = useGetUpcomingDeliveryGroups({
    buyerPostcode,
    enabled: Boolean(buyerPostcode),
    arrivingByDate: {
      low: now,
      high: fridayOfCurrentWeek,
    },
  });

  const currentSuppliers =
    upcomingDeliveryGroups.find(
      (group) =>
        dayjs(group.orderCutOffDateTime).toISOString() === orderCutOffAt,
    )?.suppliers || [];

  const suppliers = currentSuppliers.length > 0 ? currentSuppliers : [];

  const firstThreeSuppliers = suppliers.slice(0, 3);
  const remainingSuppliers = suppliers.slice(3).length;

  const renderSupplierMessage = () => {
    return (
      <>
        Get{" "}
        {firstThreeSuppliers.length > 0
          ? firstThreeSuppliers.map((s) => s.Business.name).join(", ")
          : "it"}{" "}
        {remainingSuppliers > 0 ? (
          <>
            and{" "}
            <span className="underline">{`${remainingSuppliers} more`}</span>
          </>
        ) : (
          ""
        )}{" "}
        delivered by the weekend
      </>
    );
  };

  const renderSupplierAvatars = () => {
    return firstThreeSuppliers.map((supplier) => (
      <img
        key={supplier.Business.id}
        src={supplier.Business.logoUrl || ""}
        className="h-6 w-6 rounded-full"
      />
    ));
  };

  const uniqueOrderCutOffDateTimes = [
    ...new Set(
      upcomingDeliveryGroups.map((group) => group.orderCutOffDateTime),
    ),
  ];

  if (deliveredBySellarOption !== "weekend") {
    return null;
  }

  if (isMobile) {
    return (
      <InputContainer
        className={twMerge(
          "w-fit border-none bg-[#A34FD2] text-white",
          inputContainerClassName,
        )}
      >
        <InputSelect
          value={orderCutOffAt}
          onChange={(e) => {
            setOrderCutOffAt(e.target.value);
          }}
          className="border-none text-inherit"
        >
          <InputOption value="">All cut-off times</InputOption>
          {uniqueOrderCutOffDateTimes.map((orderCutOffAtDateTime) => {
            const dateTime = dayjs(orderCutOffAtDateTime).toISOString();
            return (
              <InputOption key={dateTime} value={dateTime}>
                {dayjs(orderCutOffAtDateTime).calendar(dayjs(), {
                  sameDay: "[Cut-off] h:mma [today]",
                  nextDay: "[Cut-off] h:mma [tomorrow]",
                  nextWeek: "[Cut-off] h:mma dddd",
                  lastDay: "[Cut-off] h:mma [yesterday]",
                  lastWeek: "[Cut-off] h:mma dddd",
                  sameElse: "[Cut-off] h:mma dddd",
                })}
              </InputOption>
            );
          })}
        </InputSelect>
      </InputContainer>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 rounded-lg bg-[#E8D3F4A8] px-4 py-2">
        <div className="flex flex-1 items-center gap-2">
          <div className="flex items-center -space-x-2">
            {renderSupplierAvatars()}
          </div>
          <div>{renderSupplierMessage()}</div>
        </div>
        <InputContainer
          className={twMerge(
            "border-[#D1A7E9] bg-[#E8D3F4]",
            inputContainerClassName,
          )}
        >
          <InputSelect
            value={orderCutOffAt}
            onChange={(e) => {
              setOrderCutOffAt(e.target.value);
            }}
          >
            <InputOption value="">All cut-off times</InputOption>
            {uniqueOrderCutOffDateTimes.map((orderCutOffAtDateTime) => {
              const dateTime = dayjs(orderCutOffAtDateTime).toISOString();
              return (
                <InputOption key={dateTime} value={dateTime}>
                  {dayjs(orderCutOffAtDateTime).calendar(dayjs(), {
                    sameDay: "[Cut-off] h:mma [today]",
                    nextDay: "[Cut-off] h:mma [tomorrow]",
                    nextWeek: "[Cut-off] h:mma dddd",
                    lastDay: "[Cut-off] h:mma [yesterday]",
                    lastWeek: "[Cut-off] h:mma dddd",
                    sameElse: "[Cut-off] h:mma dddd",
                  })}
                </InputOption>
              );
            })}
          </InputSelect>
        </InputContainer>
      </div>
    </div>
  );
};

const DeliveredBySellarSingleSupplierHeader = ({
  supplierId,
  isMobile,
}: {
  supplierId: number;
  isMobile: boolean;
}) => {
  const { setSelectedSuppliers } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setSelectedSuppliers: state.setSelectedSuppliers,
    })),
  );
  const { business } = useSellarCurrentBusiness();

  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    unAuthorisedUserPostcodeInLocalStorageKey,
    undefined,
  );
  const buyerPostcode =
    business?.DeliveryAddress.postalCode || unAuthorisedUserPostcode || "";

  const [openDialog, setOpenDialog] = useState(false);
  const { data: supplier, isPending: isFetchingSupplier } =
    useSupplier(supplierId);

  const { data: upcomingDeliveries, isPending: isFetchingUpcomingDeliveries } =
    useSellarDistributionAvailabilityUpcomingDeliveryList({
      query: [
        {
          type: "supplierId",
          modifier: "EQUALS",
          value: supplierId,
        },
        {
          type: "retailerPostcode",
          modifier: "EQUALS",
          value: buyerPostcode,
        },
      ],
      limit: 1,
    });

  const nextDelivery = upcomingDeliveries?.pages.flat()[0];

  if (isFetchingSupplier || isFetchingUpcomingDeliveries) {
    return null;
  }

  const businessName = supplier?.Business.name;

  return (
    <>
      <Card
        onClick={() => setOpenDialog(true)}
        className="flex items-center justify-between rounded-md bg-zinc-50 p-4"
      >
        <div className="flex items-center gap-4">
          <ProfilePicture size="medium" logoUrl={supplier?.Business.logoUrl} />
          <div className="flex flex-col">
            <p className="inline-flex items-center gap-2 text-xl font-semibold">
              {businessName}
              <Icon size={0.6} icon="INFORMATION_OUTLINE" />
            </p>
            {nextDelivery && (
              <p className="text-sm text-zinc-700">
                Order by{" "}
                <span className="underline">
                  {dayjs(nextDelivery.orderCutOffDateTime).format("hh:mma")}
                </span>{" "}
                on{" "}
                <span className="underline">
                  {dayjs(nextDelivery.orderCutOffDateTime).format("ddd DD MMM")}
                </span>{" "}
                for delivery by Sellar on{" "}
                <span className="font-semibold text-[#8C23C7]">
                  {dayjs(nextDelivery.deliveryDate).format("ddd DD MMM")}
                </span>
              </p>
            )}
          </div>
        </div>
        {isMobile ? (
          <Icon
            onClick={() => {
              setSelectedSuppliers([]);
            }}
            icon="CLOSE"
          />
        ) : (
          <OrderBySelector />
        )}
      </Card>
      <SingleSupplierSearchDialogProvider
        open={openDialog}
        onDismiss={() => setOpenDialog(false)}
        supplierId={supplierId}
      >
        {({ supplier, onDismiss }) => (
          <SingleSupplierSearchDialogContent
            supplier={supplier}
            onDismiss={onDismiss}
          />
        )}
      </SingleSupplierSearchDialogProvider>
    </>
  );
};

export const SingleSupplierSearchDialogProvider = ({
  supplierId,
  open,
  children,
  onDismiss,
}: {
  supplierId: number | null;
  open: boolean;
  children: (params: {
    supplier: SupplierGetResponseType;
    onDismiss: () => void;
  }) => React.ReactNode;
  onDismiss: () => void;
}) => {
  const { data: supplier } = useSupplier(supplierId ?? undefined, {
    enabled: !!supplierId,
  });

  return (
    <SingleFieldDialog active={open} onDismiss={onDismiss} size="large">
      {supplier && children({ supplier: supplier, onDismiss })}
    </SingleFieldDialog>
  );
};

export const SingleSupplierSearchDialogContent = ({
  supplier,
  onDismiss,
}: {
  supplier: SupplierGetResponseType;
  onDismiss: () => void;
}) => {
  const businessName = supplier.Business.name;
  const {
    canAddToFavourites,
    isAdded: isAddedToFavourites,
    handleAddToFavourites,
  } = useSupplierAddToFavourites({ supplierId: supplier.id });

  const { isAdded, handleAddToList } = useSupplierAddToList({
    supplierId: supplier.id,
  });
  return (
    <>
      <SingleFieldDialogImage
        src={supplier.imageUrl ?? ""}
        fallback="supplier-hero"
      />
      <div className="flex items-start gap-2">
        <div className="h-12 w-12">
          <ImageWithFallback
            img={supplier.Business.logoUrl ?? ""}
            fallback="product-badge"
          />
        </div>
        <div className="flex flex-1 flex-col gap-4">
          <div>
            <button
              type="button"
              onClick={() => onDismiss()}
              className="group flex cursor-pointer items-center gap-1"
            >
              <H5 className="group-hover:underline">{businessName}</H5>
              <Icon className="relative top-0.5" icon="CHEVRON_RIGHT" />
            </button>
            <H6>
              {supplier.Business.DeliveryAddress.city}
              {supplier.Business.DeliveryAddress.region
                ? `, ${supplier.Business.DeliveryAddress.region}`
                : ""}
            </H6>
          </div>
          <div className="flex flex-1 flex-col text-zinc-500">
            <p>{supplier.Business.registeredCompanyName}</p>
            {supplier.Business.vatNumber && (
              <p>VAT {supplier.Business.vatNumber}</p>
            )}
            {supplier.Business.awrsNumber && (
              <p>AWRS {supplier.Business.awrsNumber}</p>
            )}
          </div>
          <div className="flex gap-2">
            <DataButton
              active={isAdded}
              className={"w-fit text-zinc-800"}
              onClick={handleAddToList}
              border={true}
            >
              <Icon icon={isAdded ? "CHECK" : "PLUS"} />
              {isAdded ? "Added" : "Add to list"}
            </DataButton>
            {canAddToFavourites && (
              <DataButton
                active={isAddedToFavourites}
                className={"w-fit text-zinc-800"}
                onClick={handleAddToFavourites}
                border={true}
              >
                {isAddedToFavourites ? (
                  <Icon icon={"HEART"} className="text-purple-800" />
                ) : (
                  <>
                    Add to favourites
                    <Icon icon={"HEART_OUTLINE"} className="text-purple-800" />
                  </>
                )}
              </DataButton>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ProfilePictureCollection className="flex flex-row">
            {supplier?.supplierProfileDetails.map((user, index) => (
              <ProfilePictureCollectionImage
                key={index}
                imageUrl={user.profileImageUrl}
                name={user.name}
              />
            ))}
          </ProfilePictureCollection>
          <H5>{supplier.supplierRunByString}</H5>
        </div>
      </div>
    </>
  );
};
